import { useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import axios from 'axios';
import { ChatMessage, ChatMessageAttachment, ChatUploadSummary } from '../../interfaces/ChatInterface';
import { getChatConversation, postChatStream, postChatSummary } from '../../services/chatService';
import useErrorModal from '../hooks/useErrorModal';
import useMetricsCatalog from '../hooks/useMetricsCatalog';
import CustomButton from '../reusable/CustomButton';
import ChatBreadcrumbs from './ChatBreadcrumbs';
import ChatDataExplorer, { DataExplorerRequest } from './ChatDataExplorer';
import ChatInput from './ChatInput';
import ChatMessageList from './ChatMessageList';
import { INITIAL_STREAMING_ACTIVITY, streamingActivityReducer, toolLabel } from './chatUtils';

interface ChatConversationViewProps {
  eoaId: string;
  accessToken: string;
  scope: string;
  title?: string;
  initialPrompt?: string;
  initialAttachments?: ChatMessageAttachment[];
  onBack: () => void;
  onNewChat: () => void;
  onConversationChanged: () => void;
}

const findScrollableAncestor = (el: HTMLElement): HTMLElement | null => {
  let node = el.parentElement;
  while (node) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
    node = node.parentElement;
  }
  return document.documentElement;
};

const ChatConversationView = ({
  eoaId,
  accessToken,
  scope,
  title,
  initialPrompt,
  initialAttachments,
  onBack,
  onNewChat,
  onConversationChanged,
}: ChatConversationViewProps) => {
  const { handleOpen: handleOpenErrorModal } = useErrorModal();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);
  const [input, setInput] = useState('');
  const [isPending, setIsPending] = useState(false);
  const [activity, dispatchActivity] = useReducer(streamingActivityReducer, INITIAL_STREAMING_ACTIVITY);
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [isDataExplorerOpen, setIsDataExplorerOpen] = useState(false);
  const [dataExplorerRequest, setDataExplorerRequest] = useState<DataExplorerRequest | null>(null);
  const metricOptions = useMetricsCatalog(eoaId, accessToken);

  const scrollRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const summaryAbortRef = useRef<AbortController | null>(null);
  const initializedScopeRef = useRef<string | null>(null);
  // Autoscroll turns off as soon as the user scrolls away from the bottom, and back on
  // once they scroll back to the bottom.
  const autoScrollEnabledRef = useRef(true);
  const lastScrollTopRef = useRef(0);
  const scrollToBottomRef = useRef<(() => void) | null>(null);

  const runSend = useCallback(
    async (promptText: string, attachments: ChatMessageAttachment[] = []) => {
      const trimmed = promptText.trim();
      if (!trimmed) return;

      setMessages((prev) => [...prev, { role: 'user', content: trimmed, attachments }]);
      setIsPending(true);
      dispatchActivity({ type: 'reset' });

      const controller = new AbortController();
      abortRef.current = controller;
      // The backend tags text before any tool call as "thinking" (narration) and text after the
      // tools have run as "delta" (the answer). Narration + tool activity render in the gray line;
      // the answer streams token-by-token into the assistant bubble.
      try {
        const { conversation_id, response, display_messages } = await postChatStream(
          {
            prompt: trimmed,
            eoa_id: eoaId,
            scope,
            conversation_id: conversationId,
          },
          accessToken,
          {
            signal: controller.signal,
            onDelta: (text) => dispatchActivity({ type: 'delta', text }),
            onThinking: (text) => dispatchActivity({ type: 'thinking', text }),
            onToolCall: (toolName) => dispatchActivity({ type: 'toolCall', label: toolLabel(toolName) }),
            onToolResult: () => dispatchActivity({ type: 'toolResult' }),
          }
        );
        setConversationId(conversation_id);
        // Reconcile with the persisted thread so each user turn carries its stored attachments.
        setMessages((prev) =>
          display_messages.length > 0 ? display_messages : [...prev, { role: 'assistant', content: response }]
        );
        onConversationChanged();
      } catch (error) {
        if (!controller.signal.aborted) {
          handleOpenErrorModal('Chat request failed', error as Error);
        }
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
        if (!controller.signal.aborted) {
          dispatchActivity({ type: 'reset' });
          setIsPending(false);
        }
      }
    },
    [eoaId, accessToken, scope, conversationId, onConversationChanged, handleOpenErrorModal]
  );

  const runSummary = useCallback(async () => {
    if (isPending || isSummarizing) return;

    const controller = new AbortController();
    summaryAbortRef.current = controller;
    setIsSummarizing(true);
    autoScrollEnabledRef.current = true;
    try {
      const { display_messages } = await postChatSummary({ eoa_id: eoaId, scope }, accessToken, controller.signal);
      // Guard against a scope change / unmount that aborted this request mid-flight, so a
      // late response can't clobber the freshly loaded conversation.
      if (controller.signal.aborted) return;
      setMessages(display_messages);
      onConversationChanged();
    } catch (error) {
      if (!controller.signal.aborted) handleOpenErrorModal('Failed to generate summary', error as Error);
    } finally {
      if (summaryAbortRef.current === controller) summaryAbortRef.current = null;
      if (!controller.signal.aborted) setIsSummarizing(false);
    }
  }, [eoaId, scope, accessToken, isPending, isSummarizing, onConversationChanged, handleOpenErrorModal]);

  const loadHistory = useCallback(async () => {
    try {
      const data = await getChatConversation(eoaId, scope, accessToken);
      setMessages(data.display_messages);
      setConversationId(data.conversation_id);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        setMessages([]);
        setConversationId(undefined);
        return;
      }
      handleOpenErrorModal('Failed to load conversation', error as Error);
    }
  }, [eoaId, scope, accessToken, handleOpenErrorModal]);

  useEffect(() => {
    if (initializedScopeRef.current === scope) return;
    initializedScopeRef.current = scope;

    abortRef.current?.abort();
    summaryAbortRef.current?.abort();
    setMessages([]);
    setConversationId(undefined);
    setInput('');
    dispatchActivity({ type: 'reset' });
    setIsPending(false);
    setIsSummarizing(false);
    autoScrollEnabledRef.current = true;

    if (initialPrompt) {
      void runSend(initialPrompt, initialAttachments);
    } else {
      void loadHistory();
    }
  }, [scope, initialPrompt, initialAttachments, runSend, loadHistory]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
      summaryAbortRef.current?.abort();
    },
    []
  );

  useEffect(() => {
    const anchor = scrollRef.current;
    if (!anchor) return;

    const scrollable = findScrollableAncestor(anchor);
    if (!scrollable) return;

    // Small epsilon for sub-pixel rounding when checking if we're back at the bottom.
    const BOTTOM_EPSILON = 4;
    lastScrollTopRef.current = scrollable.scrollTop;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollable;
      const atBottom = scrollHeight - scrollTop - clientHeight < BOTTOM_EPSILON;
      // Any upward movement disables autoscroll right away, even by a pixel - otherwise
      // a content update mid-gesture can snap the view back to the bottom and fight the
      // user's scroll, which reads as the screen "jumping".
      if (atBottom) {
        autoScrollEnabledRef.current = true;
      } else if (scrollTop < lastScrollTopRef.current) {
        autoScrollEnabledRef.current = false;
      }
      lastScrollTopRef.current = scrollTop;
    };

    scrollable.addEventListener('scroll', handleScroll, { passive: true });
    scrollToBottomRef.current = () => {
      scrollable.scrollTop = scrollable.scrollHeight;
    };

    return () => {
      scrollable.removeEventListener('scroll', handleScroll);
      scrollToBottomRef.current = null;
    };
  }, []);

  useLayoutEffect(() => {
    if (autoScrollEnabledRef.current) {
      scrollToBottomRef.current?.();
    }
  }, [messages, isPending, activity, isSummarizing]);

  const sentUploadIds = useMemo(
    () => new Set(messages.flatMap((message) => (message.attachments ?? []).map((attachment) => attachment.id))),
    [messages]
  );

  const handleSend = (attachments: ChatUploadSummary[]) => {
    const trimmed = input.trim();
    if (!trimmed || isPending) return;
    autoScrollEnabledRef.current = true;
    setInput('');
    void runSend(
      trimmed,
      attachments.map(({ id, original_file_name, size_bytes }) => ({ id, original_file_name, size_bytes }))
    );
  };

  // Clicking a metric pill opens the panel on that metric. A fresh request object each time keeps
  // repeat clicks on the same pill meaningful.
  const handleMetricSelect = (metricValue: string) => {
    setDataExplorerRequest({ metric: metricValue });
    setIsDataExplorerOpen(true);
  };

  const handleCloseDataExplorer = () => {
    setIsDataExplorerOpen(false);
    // Otherwise reopening the panel from the toolbar would replay the last pill's query.
    setDataExplorerRequest(null);
  };

  const handleStop = () => {
    if (!abortRef.current) return;
    // Keep the answer streamed so far as the assistant message (the backend persists the same partial).
    const partial = activity.kind === 'answer' ? activity.text : '';
    abortRef.current.abort();
    if (partial.trim()) setMessages((prev) => [...prev, { role: 'assistant', content: partial }]);
    dispatchActivity({ type: 'reset' });
    setIsPending(false);
  };

  return (
    <div className="chat-conversation">
      <div className="chat-conversation-toolbar">
        <ChatBreadcrumbs currentLabel={title || 'New chat'} onOverview={onBack} />
        <div className="chat-conversation-toolbar-actions">
          <CustomButton variant="secondary" buttonText="New chat" buttonAction={onNewChat} />
          {!isDataExplorerOpen && (
            <CustomButton
              variant="text"
              buttonText="Explore data"
              icon={<ChevronRightIcon fontSize="small" />}
              iconPosition="end"
              buttonAction={() => setIsDataExplorerOpen(true)}
            />
          )}
        </div>
      </div>

      <ChatMessageList
        messages={messages}
        isPending={isPending}
        activity={activity}
        isSummarizing={isSummarizing}
        eoaId={eoaId}
        accessToken={accessToken}
        scrollRef={scrollRef}
        metricOptions={metricOptions}
        onMetricSelect={handleMetricSelect}
      />

      <div className="chat-conversation-input">
        <ChatInput
          value={input}
          onChange={setInput}
          onSend={handleSend}
          eoaId={eoaId}
          accessToken={accessToken}
          scope={scope}
          disabled={isPending}
          placeholder=""
          sentUploadIds={sentUploadIds}
          isGenerating={isPending}
          onStop={handleStop}
          canGenerateSummary={messages.some((message) => message.role === 'assistant')}
          isSummarizing={isSummarizing}
          onGenerateSummary={() => void runSummary()}
        />
      </div>

      {isDataExplorerOpen && (
        <ChatDataExplorer
          eoaId={eoaId}
          accessToken={accessToken}
          metricOptions={metricOptions}
          request={dataExplorerRequest}
          onClose={handleCloseDataExplorer}
        />
      )}
    </div>
  );
};

export default ChatConversationView;
